/**
 * Browser tasks (AMO-155), from the workspace
 * server's `/api/amoreg/workflows/browser-task*` routes
 * (`apps/server/src/amoreg/workflows/http.ts`), where the tenant guard and
 * who-may-change-what live. Nothing here decides either.
 *
 * Reads and writes go through the Workflows state's own helpers
 * (`state/workflows.ts`): per-organization atoms on the compliance stream,
 * so a colleague's save shows without a reload; a read answers `ready`,
 * `missing` or `unavailable`; a refused write carries the server's sentence.
 *
 * @module state/browserTasks
 */
import {
  BrowserTaskListResponse,
  BrowserTaskResponse,
  type BrowserTask,
  type BrowserTaskCreateRequest,
  type BrowserTaskSaveRequest,
  type BrowserTaskSummary,
} from "@amoreg/contracts";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { Atom } from "effect/unstable/reactivity";
import { useMemo } from "react";

import { appAtomRegistry } from "../rpc/atomRegistry";
import { complianceQueryAtom } from "./complianceLive";
import {
  pairKey,
  readWorkflows,
  useWorkflowAtom,
  workflowsUrl,
  writeWorkflows,
  type WorkflowRead,
  type WorkflowWrite,
} from "./workflows";

const decodeTaskList = Schema.decodeUnknownSync(BrowserTaskListResponse);
const decodeTask = Schema.decodeUnknownSync(BrowserTaskResponse);

const none = <A>(): Effect.Effect<WorkflowRead<A>> => Effect.succeed({ kind: "unavailable" });

/* ------------------------------------------------------------------ */
/* Browser tasks                                                       */
/* ------------------------------------------------------------------ */

const taskListAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId
      ? readWorkflows(workflowsUrl("browser-tasks", { organizationId }), (json) => decodeTaskList(json).tasks)
      : none<ReadonlyArray<BrowserTaskSummary>>(),
  ).pipe(Atom.withLabel(`web-amoreg:browser-tasks:${organizationId}`)),
);

const taskAtom = Atom.family((key: string) => {
  const [organizationId = "", taskId = ""] = key.split("\u0000");
  return complianceQueryAtom(
    organizationId,
    organizationId && taskId
      ? readWorkflows(workflowsUrl("browser-task", { organizationId, taskId }), (json) => decodeTask(json).task)
      : none<BrowserTask>(),
  ).pipe(Atom.withLabel(`web-amoreg:browser-task:${taskId}`));
});

/** The viewer's own browser tasks and the organization's. */
export const useBrowserTasks = (organizationId: string | null) =>
  useWorkflowAtom(useMemo(() => taskListAtom(organizationId ?? ""), [organizationId]));

/** One browser task with its definition; `taskId` null reads nothing. */
export const useBrowserTask = (organizationId: string | null, taskId: string | null) =>
  useWorkflowAtom(useMemo(() => taskAtom(pairKey(organizationId ?? "", taskId ?? "")), [organizationId, taskId]));

export function refreshBrowserTasks(organizationId: string, taskId?: string): void {
  appAtomRegistry.refresh(taskListAtom(organizationId));
  if (taskId) appAtomRegistry.refresh(taskAtom(pairKey(organizationId, taskId)));
}

export const createBrowserTask = (organizationId: string, input: BrowserTaskCreateRequest): Promise<WorkflowWrite<BrowserTask>> =>
  writeWorkflows(
    "POST",
    workflowsUrl("browser-tasks", { organizationId }),
    input,
    (json) => decodeTask(json).task,
    "The browser task could not be created. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshBrowserTasks(organizationId);
    return outcome;
  });

export type SaveBrowserTaskOutcome =
  | { readonly kind: "saved"; readonly task: BrowserTask }
  | { readonly kind: "conflict"; readonly message: string; readonly latest: BrowserTask | null }
  | { readonly kind: "failed"; readonly message: string };

/** Saves a task, naming the version it was edited from; a colleague's newer save comes back as a conflict. */
export async function saveBrowserTask(
  organizationId: string,
  taskId: string,
  input: BrowserTaskSaveRequest,
): Promise<SaveBrowserTaskOutcome> {
  const outcome = await writeWorkflows(
    "POST",
    workflowsUrl("browser-task", { organizationId, taskId }),
    input,
    (json) => decodeTask(json).task,
    "The browser task could not be saved. Try again.",
  );
  refreshBrowserTasks(organizationId, taskId);
  if (outcome.ok) return { kind: "saved", task: outcome.value };
  if (outcome.status === 409) {
    let latest: BrowserTask | null = null;
    try {
      latest = decodeTask(outcome.body).task;
    } catch {
      latest = null;
    }
    return { kind: "conflict", message: outcome.message, latest };
  }
  return { kind: "failed", message: outcome.message };
}

export const deleteBrowserTask = (organizationId: string, taskId: string) =>
  writeWorkflows(
    "DELETE",
    workflowsUrl("browser-task", { organizationId, taskId }),
    undefined,
    () => true,
    "The browser task could not be deleted.",
  ).then((outcome) => {
    if (outcome.ok) refreshBrowserTasks(organizationId);
    return outcome;
  });
